import React, { useEffect, useState } from 'react';
import { Layout } from "@/components/layout";
import Container from "@/components/ui/container";
import Breadcrumbs from "@/components/breadcrumb";
import { Separator } from "@/components/ui/separator";
import { Ads } from "@/components/ui/ads";
import { DrupalNode } from "next-drupal";
import Image from "next/image";
import { Skeleton } from "@/components/ui/skeleton";
import { IconChevronLeft, IconChevronRight, IconPlayerPlayFilled } from "@tabler/icons-react";
import { absoluteUrl, formatDate, formatTime, reloadRevive } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { HeadMetadata } from "@/components/headMetadata";
import Link from "next/link";
import { DateTime } from "luxon";
import { AudioPlayerButton } from "@/components/audioPlayer";
import ImageWithFallback from '@/components/ui/image';

const ProgrammationPage = () => {
  const [emissions, setEmissions] = useState<DrupalNode[]>([])
  const [isLoading, setLoading] = useState(true)
  const [date, setDate] = useState(DateTime.local().setZone("Pacific/Noumea"))

  const breadcrumb = [
    { label: 'Grille des programmes' }
  ];

  const currentDate = DateTime.local().setZone("Pacific/Noumea");

  const fetchRadioLive = () => {
    fetch(process.env.NEXT_PUBLIC_DRUPAL_BASE_URL + '/radio-live-view/' + (date.weekday % 7))
      .then((res) => res.json())
      .then((data) => {
        let items: DrupalNode[] = []
        data.map((item) => {
          item.dateDebut = DateTime.fromObject({ hour: item.heure_debut, minute: item.minute_debut }, { zone: 'Pacific/Noumea' })
          item.dateFin = DateTime.fromObject({ hour: item.heure_fin, minute: item.minute_fin }, { zone: 'Pacific/Noumea' })
          items.push(item)
        })
        setEmissions(items)
        setLoading(false)
      })
      .catch(error => {
        setLoading(false)
        console.log(error)
      })
  }

  useEffect(() => {
    fetchRadioLive()
    reloadRevive()
  }, []);

  useEffect(() => {
    setLoading(true)
    fetchRadioLive()
  }, [date]);

  const updateDate = (direction: 'prev' | 'next') => {
    if (direction === "prev") {
      setDate(date.minus({ days: 1 }))
    } else {
      setDate(date.plus({ days: 1 }))
    }
  }

  const isCurrentDay = () => {
    return currentDate.toISODate({ format: 'basic' }) === date.toISODate({ format: 'basic' })
  }

  return (
    <Layout>
      <HeadMetadata
        title="Grille des programmes"
        description="La grille des programmes de Radio Rythme Bleu."
      />

      <Container>
        <div className="lg:grid grid-flow-col grid-cols-3 gap-6">
          <div className="col-span-2 lg:mb-0 mb-8">
            <Breadcrumbs entries={breadcrumb} />

            <div>
              <h1 className="mb-2 text-[40px] text-foreground-dark">Grille des programmes</h1>

              <Separator className='my-4' />

              <div className="flex align-items-center gap-2 justify-center my-10">
                <Button variant="ghost" size="icon" onClick={(e) => updateDate('prev')}><IconChevronLeft /></Button>
                <div className="bg-primary text-white py-3 px-8 rounded inline-block">{formatDate(date.toString())}</div>
                <Button variant="ghost" size="icon" onClick={(e) => updateDate('next')}><IconChevronRight /></Button>
              </div>

              {
                !isLoading && emissions ?
                  (
                    emissions.map((emission, index) =>
                    (
                      <article key={"prog" + emission.nid + '_' + index} className="mb-4 grid grid-cols-5 md:grid-cols-7 gap-4">

                        <div className="col-end-2">
                          <span>{(isCurrentDay() && (emission.dateDebut < currentDate && emission.dateFin > currentDate)) ? "Direct" : formatTime(emission.dateDebut)}</span>
                          {
                            // Passé
                            (isCurrentDay() && (currentDate > emission.dateFin)) ? (
                              <Link
                                href={emission.link}
                                className={"bg-primary aspect-square w-12 rounded text-white grid place-items-center mt-2"}>
                                <IconPlayerPlayFilled size={20} />
                              </Link>
                              // Futur
                            ) : (isCurrentDay() && (currentDate < emission.dateDebut)) ? (
                              <div
                                className={"bg-primary opacity-50 aspect-square w-12 rounded text-white grid place-items-center mt-2"}>
                                <IconPlayerPlayFilled size={20} />
                              </div>
                              // En direct
                            ) : (isCurrentDay() && (emission.dateDebut < currentDate && currentDate < emission.dateFin)) ? (
                              <AudioPlayerButton
                                className={"bg-playerState-onAir aspect-square w-12 rounded text-white grid place-items-center mt-2"}>
                                <IconPlayerPlayFilled size={20} />
                              </AudioPlayerButton>
                              // Passé par défaut
                            ) : (
                              <Link
                                href={emission.link}
                                className={"bg-primary aspect-square w-12 rounded text-white grid place-items-center mt-2"}>
                                <IconPlayerPlayFilled size={20} />
                              </Link>
                            )
                          }
                          <Separator orientation='vertical' className="h-[calc(100%-84px)] ml-6 mt-1" />
                        </div>

                        <div className="bg-neutral-50 rounded-lg p-3 flex gap-3 col-start-2 col-end-8">
                          <ImageWithFallback src={absoluteUrl(emission.field_media_image)} className='w-[70px] h-[70px] md:w-[130px] object-cover rounded'
                            alt={"illustration:" + emission.title}
                            width={130}
                            height={130} />

                          <div>
                            <h2 className="text-xl text-foreground-dark">
                              <Link href={emission.link}>
                                {emission.title}
                              </Link>
                            </h2>
                            <span className="text-sm text-foreground-light">{emission.field_animateurs}</span>

                            {emission.body && (
                              <div
                                dangerouslySetInnerHTML={{ __html: emission.body }}
                                className="text-sm"
                              />
                            )}
                          </div>
                        </div>
                      </article>
                    )
                    )
                  ) : (
                    <>
                      <div className="mb-4 grid grid-cols-7 gap-4">
                        <div className="col-end-2">
                          <Skeleton className="h-[20px] w-[48px]"></Skeleton>
                          <Skeleton className="h-[48px] w-[48px] rounded mt-2"></Skeleton>
                        </div>
                        <Skeleton className="h-[155px] w-full rounded col-start-2 col-end-8" />
                      </div>

                      <div className="mb-4 grid grid-cols-7 gap-4">
                        <div className="col-end-2">
                          <Skeleton className="h-[20px] w-[48px]"></Skeleton>
                          <Skeleton className="h-[48px] w-[48px] rounded mt-2"></Skeleton>
                        </div>
                        <Skeleton className="h-[155px] w-full rounded col-start-2 col-end-8" />
                      </div>
                    </>
                  )
              }
            </div>
          </div>

          <aside>
            <div className="sticky top-28 col-span-1 text-center">
              <Ads adsId="54" dimension="300600" className='mb-4 flex justify-center'>
              </Ads>
              <Ads adsId="55" dimension="300250">
              </Ads>

            </div>
          </aside>
        </div>
      </Container>
    </Layout>
  );
};

export default ProgrammationPage;
